import React, { useState, useEffect, useRef } from "react";
import FormTextField from "components/form/form-textfield";
import DataGrid, { DataGridItem } from "components/data-grid";
import { injectIntl, FormattedMessage } from "react-intl";
import PropTypes from "prop-types";
import Card from "components/card";
import { PageSection, PageHeaderBar } from "components/page";
import { LogoAssetForm } from "./sport-form-partial";
import ScoreboardTemplatesApiTable from "./sport-form-partial/scoreboard-templates-api-table";
import { ScoreboardTypeSelect } from "components/select";
import { ActionWithModalControlled } from "components/buttons";
import withStyles from "components/with-styles";
import styles from "./styles/sport-form.css.js";
import { getScoreboardFormPreviewUrl, getScoreboardTemplateThumbnail } from "config/paths";
import { getScoreboardTemplatesService } from "../../../../services/commonService";
import { useAuthDataContext } from "components/auth";
import TextDisplay from "../../../../components/display-data/text/text";
import Button from "@material-ui/core/Button";
import Dialog from "components/dialog";
import { useLoadingContext } from "components/loading-spinner-overlay";
import MatchPointConfigForm from "./match-point-config-form";
import { useEventFormContext } from "pages/event/forms/utils";
import { AssetApiSelectProvider } from "components/select/asset-select-context";

import {
  SCOREBOARD_TYPE_AUTOMATIC,
  SCOREBOARD_TYPE_MANUAL,
  SCOREBOARD_TYPE_NO_SCOREBOARD,
  TEAM_1,
  TEAM_2,
} from "./sport-form-constants";


const isManualScoreboardType = sportEventData =>
  sportEventData && sportEventData.scoreboardType && sportEventData.scoreboardType.includes(SCOREBOARD_TYPE_MANUAL);

const isAutomaticScoreboardType = sportEventData =>
  sportEventData && sportEventData.scoreboardType && sportEventData.scoreboardType.includes(SCOREBOARD_TYPE_AUTOMATIC);

const hasOnlyNoScoreboardType = types =>
  types?.scoreboard_type?.length === 1 && types?.scoreboard_type[0] === SCOREBOARD_TYPE_NO_SCOREBOARD;

const getTeamLogo = (number, assetId, assetBase64) => {
  if (assetId) {
    return { [`teamLogo${number}Id`]: assetId };
  }
  if (assetBase64) {
    return { [`teamLogo${number}Base64`]: assetBase64 && assetBase64.image ? assetBase64.image.split(",")[1] : {} };
  }
  return {};
};

const getScoreboardPreview = ({ eventData, contractId, fullNameForScoreboard, token }) => {
  if (!eventData.scoreboardTemplateId) {
    return Promise.resolve("");
  }

  const body = {
    templateId: eventData.scoreboardTemplateId,
    teamName1: eventData.teamName1 || "",
    teamName2: eventData.teamName2 || "",
    teamName1Abreviated: eventData.teamName1Abreviated || "",
    teamName2Abreviated: eventData.teamName2Abreviated || "",
    ...getTeamLogo(1, eventData.teamLogo1, eventData.teamLogoAsset1),
    ...getTeamLogo(2, eventData.teamLogo2, eventData.teamLogoAsset2),
  };

  return fetch(getScoreboardFormPreviewUrl({ token, contractId }), {
    method: "POST",
    body: JSON.stringify(body),
    headers: {
      "Content-Type": "application/json",
      authorization: `Bearer ${token}`,
    },
    mode: "cors",
  })
    .then(response => {
      if (!response.ok) {
        throw new Error("Error fetching scoreboard preview", response);
      }
      return response.blob();
    })
    .then(
      imageData =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(imageData);
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = error => reject(error);
        }),
    );
};

const SportForm = ({ eventToEdit = {}, isNewForm, classes, intl, values, handleChange, eventCustomerId }) => {
  const { token } = useAuthDataContext();
  const { showSportDataConfig, setShowSportDataConfig } = useEventFormContext();
  const [open, setOpen] = useState(false);
  const [hasOnlyAutomaticBurnt, setHasOnlyAutomaticBurnt] = useState(false);
  const [hasOnlyNoScoreboard, setHasOnlyNoScoreboard] = useState(false);
  const [openSelectorModal, setOpenSelectorModal] = useState(false);
  const { setLoading } = useLoadingContext();
  const [scoreboardIdSelected, setScoreboardIdSelected] = useState(() =>
    values.sportEventData ? values.sportEventData.scoreboardTemplateId : null,
  );
  const [scoreboardNameSelected, setScoreboardNameSelected] = useState("");

  const [scoreboardPreviewBase64, setScoreboardPreviewBase64] = useState("");

  useEffect(
    () => {
      if (values.sportEventData && values.sportEventData.scoreboardType) {
        setShowSportDataConfig(
          isManualScoreboardType(values.sportEventData) ||
            (isAutomaticScoreboardType(values.sportEventData) && !hasOnlyAutomaticBurnt),
        );
      }
    },
    [hasOnlyAutomaticBurnt, setShowSportDataConfig, values.sportEventData],
  );

  const handleScoreboardTypes = data => {
    setHasOnlyNoScoreboard(hasOnlyNoScoreboardType(data?.types));
    setHasOnlyAutomaticBurnt(data && data?.onlyAutomaticBurnt);
  };

  const handleOpen = () => {
    setLoading(true);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleChangeScoreboardType = e => handleChange("sportEventData.scoreboardType")(e.target.value);

  const contractId = values.contractId || eventToEdit.contractId;
  const eventTypeId = values.typeId || eventToEdit.typeId;
  const roomTemplateId = values.roomTemplateId || eventToEdit.roomTemplateId;
  const scoreboardTemplateId = values.sportEventData?.scoreboardTemplateId;

  // Al crear un evento nuevo (no al editar), si aún no hay plantilla de marcador elegida,
  // se autoselecciona la marcada como "default" — igual que hacía el antiguo
  // ScoreboardTemplateSelect (GraphQL, `getDefaultItemSelected`) antes de que sport-form.jsx
  // pasara al patrón de miniatura + modal, que no traía esa autoselección.
  useEffect(
    () => {
      if (!isNewForm || !showSportDataConfig || !contractId || !eventTypeId || scoreboardTemplateId) {
        return;
      }
      getScoreboardTemplatesService(contractId, eventTypeId, templates => {
        const defaultTemplate = (templates || []).find(template => template.default);
        if (defaultTemplate) {
          handleChange("sportEventData.scoreboardTemplateId")(defaultTemplate.template_id);
          setScoreboardNameSelected(defaultTemplate.codename);
        }
      });
    },
    [isNewForm, showSportDataConfig, contractId, eventTypeId, scoreboardTemplateId, handleChange],
  );

  useEffect(
    () => {
      const eventData = values.sportEventData;
      if (!eventData || !eventData.scoreboardTemplateId) {
        setScoreboardPreviewBase64("");
      } else if (eventData && eventData.scoreboardTemplateId) {
        getScoreboardPreview({
          eventData: values.sportEventData,
          contractId,
          token,
        })
          .then(preview => setScoreboardPreviewBase64(preview))
          .catch(error => {
            setScoreboardPreviewBase64("");
            console.error(error);
          });
      }
    },
    [contractId, token, values.sportEventData],
  );

  return values ? (
    <AssetApiSelectProvider>
      <div>
        <PageHeaderBar subtitle title="app.page.event.form.sportData" />
        <PageSection hideBackground>
          <DataGrid>
            <Card>
              {!(contractId && eventTypeId && roomTemplateId) && <FormattedMessage id="event.scoreboardType.noData" />}
              {contractId && eventTypeId && roomTemplateId && !hasOnlyNoScoreboard && (
                <DataGridItem big>
                  <ScoreboardTypeSelect
                    id="sportEventData.scoreboardType"
                    name="sportEventData.scoreboardType"
                    label={intl.formatMessage({ id: "event.scoreboardType" })}
                    handleChange={handleChangeScoreboardType}
                    contractId={contractId}
                    eventTypeId={eventTypeId}
                    roomTemplateId={roomTemplateId}
                    value={values.sportEventData ? values.sportEventData.scoreboardType : ""}
                    onLoadQuery={data => {
                      handleScoreboardTypes(data?.scoreboardTypes);
                    }}
                  />
                </DataGridItem>
              )}
              {isManualScoreboardType(values.sportEventData) && (
                <DataGridItem big>
                  <FormattedMessage id="event.scoreboardType.manual.info" />
                </DataGridItem>
              )}
              {isAutomaticScoreboardType(values.sportEventData) && hasOnlyAutomaticBurnt && (
                <DataGridItem big>
                  <FormattedMessage id="event.scoreboardType.automaticrsi.only.info" />
                </DataGridItem>
              )}
              {showSportDataConfig && (
                <>
                  <DataGridItem big className={classes.searchBox}>
                    <DataGrid>
                      <DataGridItem big className={classes.scoreboardTemplateSelector}>
                        <div className={classes.selectedScoreboardTemplateSelector}>
                          <TextDisplay
                            className={classes.data}
                            label={intl.formatMessage({ id: "event.selectedScoreboardTemplate" })}
                          >
                            {scoreboardNameSelected}
                          </TextDisplay>
                          <img
                            className={classes.preview_image}
                            src={getScoreboardTemplateThumbnail({
                              contractId,
                              scoreboardTemplateId: values.sportEventData?.scoreboardTemplateId,
                              token,
                            })}
                            alt={values.sportEventData?.scoreboardTemplateId}
                          />
                        </div>
                        <Button onClick={() => setOpenSelectorModal(true)} variant="contained" color="primary">
                          {intl.formatMessage({ id: "event.scoreboardTemplate" })}
                        </Button>
                      </DataGridItem>
                    </DataGrid>
                    <ActionWithModalControlled
                      open={openSelectorModal}
                      setOpen={setOpenSelectorModal}
                      onOpen={() =>
                        setScoreboardIdSelected(
                          values.sportEventData ? values.sportEventData.scoreboardTemplateId : null,
                        )
                      }
                      onClose={() => setScoreboardIdSelected(null)}
                      onAccept={() => handleChange("sportEventData.scoreboardTemplateId")(scoreboardIdSelected || null)}
                      dialogTitle={intl.formatMessage({ id: "event.scoreboardTemplate" })}
                      icon="search"
                      renderDialogContent={() => (
                        <DataGrid>
                          <DataGridItem big>
                            <ScoreboardTemplatesApiTable
                              contractId={contractId}
                              eventTypeId={eventTypeId}
                              scoreboardIdSelected={scoreboardIdSelected}
                              setScoreboardIdSelected={setScoreboardIdSelected}
                              setScoreboardNameSelected={setScoreboardNameSelected}
                            />
                          </DataGridItem>
                        </DataGrid>
                      )}
                    />
                  </DataGridItem>
                  {scoreboardPreviewBase64 && (
                    <DataGridItem big>
                      <PageHeaderBar subtitle title="event.sportEventData.sportData.scoreboard.preview" />
                      <div className={classes.scoreboardPreview_container}>
                        <img
                          onClick={handleOpen}
                          src={scoreboardPreviewBase64}
                          id="scoreboardPreview"
                          className={classes.scoreboardPreview}
                        />
                        <Dialog showCloseButton open={open} onClose={handleClose}>
                          <img
                            className={classes.scoreboardPreview_image}
                            onLoad={() => setLoading(false)}
                            src={scoreboardPreviewBase64}
                          />
                        </Dialog>
                      </div>
                    </DataGridItem>
                  )}
                </>
              )}
            </Card>
          </DataGrid>
          {showSportDataConfig && (
            <>
              <DataGrid>
                <Card>
                  <DataGridItem>
                    <FormTextField
                      type="text"
                      id="sportEventData.teamName1Abreviated"
                      name="sportEventData.teamName1Abreviated"
                      value={values.sportEventData ? values.sportEventData.teamName1Abreviated : ""}
                      onChange={handleChange}
                      label={intl.formatMessage({ id: "event.sportEventData.teamName1Abreviated" })}
                      inputProps={{
                        maxLength: 4,
                      }}
                    />
                  </DataGridItem>
                  <DataGridItem />
                  <DataGridItem>
                    <FormTextField
                      type="text"
                      id="sportEventData.teamName1"
                      name="sportEventData.teamName1"
                      value={values.sportEventData ? values.sportEventData.teamName1 : ""}
                      onChange={handleChange}
                      label={intl.formatMessage({ id: "event.sportEventData.teamName1" })}
                    />
                  </DataGridItem>
                  <DataGridItem>
                    <LogoAssetForm
                      team={TEAM_1}
                      isNewForm={isNewForm}
                      customerIdSelected={eventCustomerId}
                      assetPath="sportEventData.teamLogoAsset1"
                      assetIdPath="sportEventData.teamLogo1"
                      handleChange={handleChange}
                      assetId={values.sportEventData ? values.sportEventData.teamLogo1 : ""}
                      asset={values.sportEventData ? values.sportEventData.teamLogoAsset1 : {}}
                    />
                  </DataGridItem>
                </Card>
              </DataGrid>
              <DataGrid>
                <Card>
                  <DataGridItem>
                    <FormTextField
                      type="text"
                      id="sportEventData.teamName2Abreviated"
                      name="sportEventData.teamName2Abreviated"
                      value={values.sportEventData ? values.sportEventData.teamName2Abreviated : ""}
                      onChange={handleChange}
                      inputProps={{
                        maxLength: 4,
                      }}
                      label={intl.formatMessage({ id: "event.sportEventData.teamName2Abreviated" })}
                    />
                  </DataGridItem>
                  <DataGridItem />
                  <DataGridItem>
                    <FormTextField
                      type="text"
                      id="sportEventData.teamName2"
                      name="sportEventData.teamName2"
                      value={values.sportEventData ? values.sportEventData.teamName2 : ""}
                      onChange={handleChange}
                      label={intl.formatMessage({ id: "event.sportEventData.teamName2" })}
                    />
                  </DataGridItem>
                  <DataGridItem>
                    <LogoAssetForm
                      team={TEAM_2}
                      isNewForm={isNewForm}
                      customerIdSelected={eventCustomerId}
                      assetPath="sportEventData.teamLogoAsset2"
                      assetIdPath="sportEventData.teamLogo2"
                      handleChange={handleChange}
                      assetId={values.sportEventData ? values.sportEventData.teamLogo2 : ""}
                      asset={values.sportEventData ? values.sportEventData.teamLogoAsset2 : {}}
                    />
                  </DataGridItem>
                </Card>
              </DataGrid>
            </>
          )}
        </PageSection>
        <MatchPointConfigForm handleChange={handleChange} values={values} />
      </div>
    </AssetApiSelectProvider>
  ) : (
    ""
  );
};

SportForm.propTypes = {
  /**
   * Otros datos del evento a editar que no se envían en el form
   */
  eventToEdit: PropTypes.object,
  /**
   * Indica si es un formulario nuevo
   */
  isNewForm: PropTypes.bool,
  /**
   * Customer id del contrato del evento
   */
  eventCustomerId: PropTypes.string,
  /**
   * Valores del formulario
   */
  values: PropTypes.object,
  /**
   * Callback que se dispara con el cambio del input
   */
  handleChange: PropTypes.func,
};

export default injectIntl(withStyles(styles)(SportForm));
